import React, { useCallback, useEffect, useState } from "react";
import { useEstimateApi } from "../../hooks/useEstimateApi.ts";
import { Alert, Box, CircularProgress, Stack } from "@mui/material";
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import EstimatesList from "./EstimateList.tsx";
import EstimateSearchBar from "../../components/common/EstimateSearchBar.tsx";
import {
  EstimateSearchCriteria,
  EstimateSearchResponse,
  EstimateSortField,
  defaultCriteria,
  defaultPagination,
} from "../../contracts/estimates/searchTypes.ts";
import { designTokens } from "../../theme.ts";
import ConsoleHeader, {
  ConsoleHeaderMeta,
  LedgerFrame,
} from "../../components/common/ConsoleHeader.tsx";

const SORT_FIELD_WHITELIST: EstimateSortField[] = [
  "createdTimestamp",
  "estimateDate",
  "companyName",
  "companyInCharge",
];

function isAllowedSortField(value: string): value is EstimateSortField {
  return (SORT_FIELD_WHITELIST as string[]).includes(value);
}

export default function EstimatePage() {
  const { searchEstimates } = useEstimateApi();
  const [criteria, setCriteria] = useState<EstimateSearchCriteria>(defaultCriteria);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: defaultPagination.pageSize,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "createdTimestamp", sort: "desc" },
  ]);
  const [response, setResponse] = useState<EstimateSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sort = sortModel[0];
      const sortField =
        sort && isAllowedSortField(sort.field) ? sort.field : "createdTimestamp";
      const sortOrder: "asc" | "desc" = sort?.sort === "asc" ? "asc" : "desc";

      const json = await searchEstimates(criteria, {
        page: paginationModel.page + 1, // DataGrid is 0-based, API is 1-based
        pageSize: paginationModel.pageSize,
        sortField,
        sortOrder,
      });
      setResponse(json);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, [criteria, paginationModel, sortModel, searchEstimates]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (newCriteria: EstimateSearchCriteria) => {
    setCriteria(newCriteria);
    setPaginationModel((p) => ({ ...p, page: 0 })); // back to first page
  };

  const handleReset = () => {
    setCriteria(defaultCriteria);
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const total = response?.total ?? 0;

  return (
    <Box sx={{ fontFamily: designTokens.fonts.sans }}>
      <ConsoleHeader
        code="MARS · ESTIMATE · INDEX"
        title="견적서 조회"
        meta={<ConsoleHeaderMeta recordCountLabel="견적서" count={total} />}
      />

      <EstimateSearchBar onSearch={handleSearch} onReset={handleReset} />

      {error && (
        <Alert severity="error" sx={{ mb: 2, fontFamily: designTokens.fonts.sans }}>
          {error}
        </Alert>
      )}

      {!response && !error && <FetchingState label="견적서를 불러오는 중입니다" />}

      {response && (
        <LedgerFrame label="견적 원장" rows={total}>
          <Stack>
            <EstimatesList
              data={response.data}
              total={response.total}
              isLoading={loading}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              onRefresh={fetchData}
            />
          </Stack>
        </LedgerFrame>
      )}
    </Box>
  );
}

function FetchingState({ label }: { label: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        py: 8,
      }}
    >
      <CircularProgress size={28} sx={{ color: designTokens.cargo }} />
      <Box
        sx={{
          fontFamily: designTokens.fonts.sans,
          fontSize: 14,
          color: "#6B7178",
        }}
      >
        {label}
      </Box>
    </Box>
  );
}
